import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";
import { isReaction } from "@/shared/lib/feedbackReaction";
import type { NuggetFeedback, NuggetReaction } from "@/features/nuggets/model/nuggetsSlice";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toNuggetFeedback(data: unknown): NuggetFeedback {
  if (!isRecord(data)) {
    throw new Error("Unexpected response shape.");
  }
  return {
    // `reaction` comes back null when the click toggled the reaction off.
    reaction: isReaction(data.reaction) ? data.reaction : null,
    comment: typeof data.comment === "string" ? data.comment : null,
  };
}

/** Records the reaction immediately — independent of whether a comment is ever submitted.
 * Sending the reaction the user already has clears it, unless a comment is attached. */
export async function setNuggetReaction(
  nuggetId: number,
  reaction: NuggetReaction,
): Promise<NuggetFeedback> {
  const res = await apiFetch(`/v1/nuggets/${nuggetId}/feedback`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reaction }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return toNuggetFeedback(await res.json());
}

/** Attaches text to the reaction already recorded, replacing any previous comment. */
export async function addNuggetFeedbackComment(
  nuggetId: number,
  comment: string,
): Promise<NuggetFeedback> {
  const res = await apiFetch(`/v1/nuggets/${nuggetId}/feedback`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return toNuggetFeedback(await res.json());
}

/** Removes the reaction. The backend rejects this with 409 while a comment is attached. */
export async function clearNuggetReaction(nuggetId: number): Promise<void> {
  const res = await apiFetch(`/v1/nuggets/${nuggetId}/feedback`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
